/**
 * The register lane's toolbar (SA-462, spec §8.1, D13): the view switch on
 * the left; on the right the search, a Filters button whose badge counts
 * the active filters, the active filters as removable chips (a chip that
 * states a fact should also let you drop it), `Showing N of M` and Priority,
 * the one sort no column names (SA-1124: the headers sort the rest).
 * SA-1184: a module's register view rides as a toggle just before Priority. The
 * stats bar and the queue tiles are not here — their counts ride the
 * panel's QUEUE chips. Mounted only on the register lane: it calls
 * `useStatusMap` for the status words.
 */
import type { ReactNode } from 'react'
import { Columns3, ListOrdered, Search, SlidersHorizontal, X } from 'lucide-react'
import { cn } from '@/utils'
import { Button, Input } from '@/components/ui'
import { useStatusMap } from '@/hooks/useRecordExtras'
import { STATUS_CONFIG } from '@/components/orders/orderStatusConfig'
import { QUEUE_STATE_CONFIG, WAITING_ON_LABELS } from '@/constants/queueStates'
import {
  activeFilterChips, clearFilter, type RegisterFilterSetters, type RegisterFilterState, type StepOption,
} from './registerFilterModel'
import { tourAnchor } from '@/components/tour/tourAnchor'

export interface RegisterToolbarProps {
  viewSwitch: ReactNode
  noun: { singular: string; plural: string }
  searchQuery: string
  onSearch: (query: string) => void
  filters: RegisterFilterState
  setters: RegisterFilterSetters
  steps: StepOption[]
  /** The filtered customer's name when a loaded row carries it. */
  contactName: string | null
  filtersOpen: boolean
  onToggleFilters: () => void
  panelId: string
  /** `orders.length` of `useOrders`' `total` — the server's filtered count. */
  showing: number
  total: number
  /** SA-1124: whether the queue's priority ranking orders the list. */
  priority?: boolean
  /** SA-1124: Priority on or off; absent = no Priority toggle. */
  onPriority?: (on: boolean) => void
  /** SA-1184: the register views (manifest `REGISTER_VIEWS`), each a toggle
   *  beside Priority; the page passes them only on the register lane. */
  viewToggles?: { id: string; label: string }[]
  /** SA-1184: the mounted view's id, or null while the register shows. */
  activeView?: string | null
  /** SA-1184: a toggle pressed on (its id) or off (null); absent = no toggles. */
  onViewToggle?: (id: string | null) => void
}

const ORDER_STATUS_LABELS = Object.fromEntries(Object.entries(STATUS_CONFIG).map(([k, v]) => [k, v.label]))
const QUEUE_LABELS = Object.fromEntries(Object.entries(QUEUE_STATE_CONFIG).map(([k, v]) => [k, v.label]))
// SA-1124: a pressed toggle is outlined in primary; the dark border holds over
// the basis outline variant's own (F5). SA-1184: Priority and the view toggles share it.
const PRESSED = 'border-primary text-primary hover:text-primary dark:border-primary'

export function RegisterToolbar({
  viewSwitch, noun, searchQuery, onSearch, filters, setters, steps, contactName,
  filtersOpen, onToggleFilters, panelId, showing, total, priority = false, onPriority,
  viewToggles = [], activeView = null, onViewToggle,
}: RegisterToolbarProps) {
  const statusMap = useStatusMap()
  const chips = activeFilterChips(filters, {
    steps, statuses: statusMap.data?.labels ?? {}, orderStatuses: ORDER_STATUS_LABELS, queueStates: QUEUE_LABELS, waitingOn: WAITING_ON_LABELS, contactName,
    quoteStatuses: { prepared: 'prepared', sent: 'sent', countered: 'countered', accepted: 'accepted', declined: 'declined' },
  })
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {viewSwitch}
      <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input type="text" aria-label={`Search ${noun.plural.toLowerCase()}`} placeholder={`Search ${noun.plural.toLowerCase()}...`}
            value={searchQuery} onChange={(e) => onSearch(e.target.value)} className="h-8 pl-9" {...tourAnchor('records.toolbar.search')} />
        </div>
        {/* The badge is decoration once the name carries the count (SA-464 c).
            SA-1124: the basis outline button; open, it holds the hover look. */}
        <Button type="button" variant="outline" size="sm" aria-expanded={filtersOpen} aria-controls={panelId}
          onClick={onToggleFilters} {...tourAnchor('records.toolbar.filters')}
          aria-label={chips.length > 0 ? `Filters, ${chips.length} active` : 'Filters'}
          className={cn(filtersOpen && 'bg-accent text-accent-foreground dark:bg-accent dark:hover:bg-accent')}>
          <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
          Filters
          {chips.length > 0 && (
            <span className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-primary px-1.5 text-[11px] text-primary-foreground">
              {chips.length}
            </span>
          )}
        </Button>
        {chips.map((chip) => (
          // SA-468: the panel's shape (one chip shape per screen), the
          // category muted beside the value.
          <span key={chip.key} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium">
            <span className="text-muted-foreground">{chip.category}:</span>{' '}
            <span className="text-foreground">{chip.value}</span>
            <Button type="button" variant="ghost" size="sm" aria-label={`Remove ${chip.label}`}
              onClick={() => clearFilter(setters, chip.key)} className="h-5 w-5 p-0">
              <X aria-hidden="true" className="size-3" />
            </Button>
          </span>
        ))}
        <span className="whitespace-nowrap text-xs text-muted-foreground">Showing {showing} of {total}</span>
        {onViewToggle && viewToggles.map((toggle, index) => {
          const on = activeView === toggle.id
          return (
            <Button key={toggle.id} type="button" variant="outline" size="sm" aria-pressed={on}
              onClick={() => onViewToggle(on ? null : toggle.id)} className={cn(on && PRESSED)}
              {...(index === 0 ? tourAnchor('records.toolbar.module-views') : {})}>
              <Columns3 aria-hidden="true" className="h-4 w-4" />
              {toggle.label}
            </Button>
          )
        })}
        {onPriority && (
          <Button type="button" variant="outline" size="sm" aria-pressed={priority}
            onClick={() => onPriority(!priority)} className={cn(priority && PRESSED)} {...tourAnchor('records.toolbar.priority')}>
            <ListOrdered aria-hidden="true" className="h-4 w-4" />
            Priority
          </Button>
        )}
      </div>
    </div>
  )
}
